import { FC, ReactNode, useState } from 'react';

import { Box, Container, rem, ToastContainerSyle, useBreakpointValue } from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { UserMobileNavbar } from '../../modules/user-navbar/components/user-mobile-navbar';
import { UserNavbar } from '../../modules/user-navbar/components/user-navbar';
import { GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT, GlobalMobileBottomNav } from '../action-bar/global-mobile-bottom-nav';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { Reveal } from '../display/reveal';
import { useSeasonalTheme } from '../seasonal';
import { useSpoofing } from './hooks/useSpoofing';

export interface AppLayoutProps {
	navbar: ReactNode;
	children: ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children, navbar }) => {
	const { data } = useSpoofing();
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const [isExpanded, setIsExpanded] = useState(true);
	const toggleSidebar = () => setIsExpanded(!isExpanded);
	// Default to mobile-first to prevent desktop sidebar flash on mobile devices
	// when useBreakpointValue hasn't yet determined the breakpoint
	const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;

	if (isHomepageRedesignEnabled) {
		const isSpoofing = data?.is_spoofing && !data?.is_demoing;
		const spoofingBannerHeight = rem(60);
		const mobileNavbarHeight = rem(56);
		const mobileBottomNavHeight = rem(GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT);

		const getMinHeight = () => {
			if (isMobile) {
				return isSpoofing ? `calc(100vh - ${spoofingBannerHeight} - ${mobileNavbarHeight})` : `calc(100vh - ${mobileNavbarHeight})`;
			}
			return isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh';
		};

		return (
			<Box bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.surface'} minH="100vh">
				<Reveal if={isSpoofing}>
					<SpoofingWarning />
				</Reveal>
				<ToastContainerSyle style={{ 'padding-top': '0px' }} />

				{/* TOP NAVBAR - Mobile only */}
				<Reveal if={isMobile}>
					<UserMobileNavbar isSpoofing={isSpoofing} />
				</Reveal>

				<Box display="flex" minH={getMinHeight()}>
					{/* USER NAVIGATION - Desktop only */}
					<Reveal if={!isMobile}>
						<Box
							position="fixed"
							left="0"
							top={isSpoofing ? spoofingBannerHeight : '0'}
							height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
							width={isExpanded ? rem(260) : rem(60)}
							bg="bg.canvas"
							zIndex="docked"
							transition="width 0.2s cubic-bezier(.4,0,.2,1)"
							overflow="hidden"
						>
							<UserNavbar isExpanded={isExpanded} toggleSidebar={toggleSidebar} />
						</Box>
					</Reveal>

					{/* CONTENTS */}
					<Box
						ml={{ base: 0, sm: isExpanded ? rem(260) : rem(60) }}
						pb={{ base: mobileBottomNavHeight, sm: 0 }}
						flex="1"
						minW="0"
						transition="margin-left 0.2s cubic-bezier(.4,0,.2,1)"
					>
						{children}
					</Box>
				</Box>

				{/* BOTTOM NAVIGATION - Mobile only */}
				<Reveal if={isMobile}>
					<GlobalMobileBottomNav />
				</Reveal>
			</Box>
		);
	}

	return (
		<Box bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.subtle'} display="flex" flexDirection="column" minH="100vh">
			<Box
				backdropFilter="blur(20px)"
				bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.topNav'}
				position="sticky"
				top="0"
				zIndex="docked"
				w="100%"
				id="nav-wrapper"
				borderBottomColor="border.default"
				borderBottomWidth="1px"
				borderBottomStyle="solid"
			>
				{data?.is_spoofing && !data?.is_demoing && <SpoofingWarning />}

				<ToastContainerSyle style={{ 'padding-top': rem(64) }} />

				<Container maxW={'100%'} px={0}>
					{navbar}
				</Container>
			</Box>

			<Box display="flex" flexDirection="column" flexGrow={1} pos="relative" zIndex="base">
				{children}
			</Box>
		</Box>
	);
};
